/**
 * GitHub Data Service for fmi.dev
 * Fetches real, verified public activity for @Maverick-list without private credentials.
 * 
 * Sources:
 * - Public user profile: https://api.github.com/users/:username
 * - Public contributions calendar: https://github-contributions-api.jogruber.de/v4/:username?y=last
 * 
 * Security & Reliability:
 * - Zero tokens or secrets exposed in browser-delivered code.
 * - Runtime data validation with strict type-checking.
 * - 1-hour localStorage TTL cache to avoid rate limiting and excessive requests.
 * - Graceful fallback to verified offline snapshot if offline or rate-limited.
 */

const GITHUB_USERNAME = "Maverick-list";
const CACHE_KEY_DATA = `fmi_github_data_${GITHUB_USERNAME}_v2`;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

// Verified fallback baseline snapshot (captured Oct 25, 2026 from live GitHub profile)
export const VERIFIED_FALLBACK_STATS = {
  username: GITHUB_USERNAME,
  profileUrl: `https://github.com/${GITHUB_USERNAME}`,
  publicRepos: 38,
  totalContributionsLastYear: 383,
  timeRange: "Oct 26, 2025 – Oct 25, 2026 (Rolling 365 Days)",
  source: "GitHub Public Activity API (@Maverick-list)",
  timezone: "UTC / WIB (GMT+7)",
  isLive: false,
  isCached: false,
};

/**
 * Validates the raw contributions payload.
 */
export function validateContributionsPayload(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (!Array.isArray(payload.contributions)) return false;
  if (typeof payload.total !== "object" || payload.total === null) return false;
  return true;
}

/**
 * Aggregates daily contributions into monthly sums.
 * Produces an array of { monthKey: 'YYYY-MM', label: 'Mon', count: number }
 */
export function aggregateMonthlyContributions(contributions) {
  if (!Array.isArray(contributions) || contributions.length === 0) {
    return [];
  }

  const monthMap = {};
  const monthOrder = [];

  for (const day of contributions) {
    if (!day || typeof day.date !== "string") continue;
    const monthKey = day.date.slice(0, 7); // 'YYYY-MM'
    const count = typeof day.count === "number" ? day.count : 0;

    if (!(monthKey in monthMap)) {
      monthMap[monthKey] = 0;
      monthOrder.push(monthKey);
    }
    monthMap[monthKey] += count;
  }

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return monthOrder.map((key) => {
    const [, monthNum] = key.split("-");
    const label = monthNames[parseInt(monthNum, 10) - 1] || key;
    return {
      monthKey: key,
      label,
      count: monthMap[key],
    };
  });
}

/**
 * Groups an array of 365 contribution days into 52/53 columns of 7 days (Sunday to Saturday).
 */
export function formatContributionGrid(contributions) {
  if (!Array.isArray(contributions)) return [];

  const columns = [];
  let currentColumn = [];

  for (let i = 0; i < contributions.length; i++) {
    const day = contributions[i];
    currentColumn.push(day);

    if (currentColumn.length === 7) {
      columns.push(currentColumn);
      currentColumn = [];
    }
  }

  if (currentColumn.length > 0) {
    columns.push(currentColumn);
  }

  return columns;
}

/**
 * Fetches verified GitHub activity with localStorage caching.
 */
export async function fetchGitHubActivity() {
  // 1. Check cache
  try {
    const cachedRaw = localStorage.getItem(CACHE_KEY_DATA);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      const isExpired = Date.now() - cached.timestamp > CACHE_TTL_MS;
      if (!isExpired && cached.data) {
        return {
          ...cached.data,
          isCached: true,
          isLive: true,
        };
      }
    }
  } catch (err) {
    console.warn("GitHub cache read error:", err);
  }

  // 2. Fetch live data
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000); // 6s timeout

    const [contribRes, userRes] = await Promise.allSettled([
      fetch(`https://github-contributions-api.jogruber.de/v4/${GITHUB_USERNAME}?y=last`, {
        signal: controller.signal,
      }),
      fetch(`https://api.github.com/users/${GITHUB_USERNAME}`, {
        signal: controller.signal,
        headers: { Accept: "application/vnd.github.v3+json" },
      }),
    ]);

    clearTimeout(timeoutId);

    let contributions = null;
    let totalContributions = VERIFIED_FALLBACK_STATS.totalContributionsLastYear;
    let publicRepos = VERIFIED_FALLBACK_STATS.publicRepos;

    if (contribRes.status === "fulfilled" && contribRes.value.ok) {
      const contribJson = await contribRes.value.json();
      if (validateContributionsPayload(contribJson)) {
        contributions = contribJson.contributions;
        totalContributions = contribJson.total?.lastYear ?? contribJson.contributions.reduce((s, c) => s + c.count, 0);
      }
    }

    if (userRes.status === "fulfilled" && userRes.value.ok) {
      const userJson = await userRes.value.json();
      if (typeof userJson.public_repos === "number") {
        publicRepos = userJson.public_repos;
      }
    }

    if (!contributions || contributions.length === 0) {
      throw new Error("Unable to parse live contributions calendar");
    }

    const monthlyCadence = aggregateMonthlyContributions(contributions);
    const gridColumns = formatContributionGrid(contributions);

    const resultData = {
      username: GITHUB_USERNAME,
      profileUrl: `https://github.com/${GITHUB_USERNAME}`,
      publicRepos,
      totalContributionsLastYear: totalContributions,
      timeRange: `${contributions[0]?.date} to ${contributions[contributions.length - 1]?.date}`,
      source: "GitHub Public Activity API (@Maverick-list)",
      timezone: "UTC",
      monthlyCadence,
      gridColumns,
      isLive: true,
      isCached: false,
    };

    // Save to cache
    try {
      localStorage.setItem(
        CACHE_KEY_DATA,
        JSON.stringify({ timestamp: Date.now(), data: resultData })
      );
    } catch {
      // Storage quota or disabled
    }

    return resultData;
  } catch (fetchError) {
    console.warn("Live GitHub fetch error, using fallback snapshot:", fetchError);
    return {
      ...VERIFIED_FALLBACK_STATS,
      error: fetchError.message,
    };
  }
}
